import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAdoption } from '../../context/AdoptionContext';
import { usePractice } from '../../context/PracticeContext';
import api from '../../services/api';
import '../../styles/Adoption.css';

function SetupBanner({ content }) {
  const { dismissContent, startWalkthrough, pageContent } = useAdoption();
  const { selectedPracticeId } = usePractice();
  const navigate = useNavigate();
  const [conditionMet, setConditionMet] = useState(true);

  // GH-113 follow-up: banners can declare cta_config.hide_when to auto-hide
  // once the practice-level prerequisite is satisfied. Prevents the
  // "Set Up Digital Intake Forms" banner from following practices around
  // forever even after they've configured their form templates.
  useEffect(() => {
    // Prefer explicit cta_config.hide_when; fall back to matching the
    // pre-existing "Set Up Digital Intake Forms" banner by title+page so
    // the fix takes effect on already-seeded practices without waiting
    // for a re-seed.
    let rule = content.cta_config?.hide_when;
    if (!rule && content.title === 'Set Up Digital Intake Forms' && content.target_page === 'care-hub') {
      rule = 'has_intake_forms';
    }
    if (!rule || !selectedPracticeId) return;
    let cancelled = false;
    (async () => {
      try {
        if (rule === 'has_intake_forms') {
          const [pkgs, tpls] = await Promise.all([
            api.getIntakePackages(selectedPracticeId).catch(() => ({ data: [] })),
            api.getIntakeTemplates(selectedPracticeId).catch(() => ({ data: [] })),
          ]);
          const configured = (pkgs.data || []).length > 0 || (tpls.data || []).length > 0;
          if (!cancelled && configured) setConditionMet(false);
        }
      } catch { /* keep banner visible on lookup failure */ }
    })();
    return () => { cancelled = true; };
  }, [content.cta_config?.hide_when, content.title, content.target_page, selectedPracticeId]);

  // GH-113: the "Set Up Digital Intake Forms" CTA appeared inert because
  // the seeded content uses `cta_config.path` (e.g. /practice-view/
  // practice-settings?tab=digital-forms) but this handler only recognized
  // `walkthrough_id` and `navigate`, so the button silently no-op'd
  // beyond dismissing. Also, `window.location.hash =` doesn't work for
  // BrowserRouter — we use react-router's navigate() now so the SPA
  // routes properly without a full page reload.
  const handleCta = () => {
    if (content.cta_config?.walkthrough_id) {
      const wt = pageContent.walkthroughs.find(w => w.id === content.cta_config.walkthrough_id);
      if (wt) {
        startWalkthrough(wt);
        return;
      }
    }
    const target = content.cta_config?.path || content.cta_config?.navigate;
    if (target) {
      // path-only ("/foo?x=1") → SPA navigate. Full URL → window.location.
      if (/^https?:\/\//i.test(target)) {
        window.location.href = target;
      } else {
        navigate(target);
      }
    }
    dismissContent(content.id);
  };

  if (!conditionMet) return null;

  return (
    <div className="adopt-banner" style={{ '--banner-color': content.cta_config?.color || '#6366f1' }}>
      <div className="adopt-banner-content">
        <div className="adopt-banner-icon">
          {content.cta_config?.icon || 'info'}
        </div>
        <div className="adopt-banner-text">
          {content.title && <strong>{content.title}</strong>}
          {content.body && <span> {content.body}</span>}
        </div>
      </div>
      <div className="adopt-banner-actions">
        {content.cta_text && (
          <button className="adopt-banner-cta" onClick={handleCta}>
            {content.cta_text}
          </button>
        )}
        <button className="adopt-banner-close" onClick={() => dismissContent(content.id)}>
          Dismiss
        </button>
      </div>
    </div>
  );
}

export default SetupBanner;
